import type { Company } from '@/constants/companies'

import { useCommandState } from 'cmdk'
import Asset from '@/components/ui/asset'
import Avatar from '@/components/ui/avatar'
import Tag from '@/components/ui/tag'
import SegmentBar from '@/components/generals/segment-bar'
import { CommandItem } from '@/components/ui/command'
import { ownerByName, tagTone } from '@/constants/companies'
import { formatDate, formatMoney, splitTags } from '@/lib/companies'
import { cn } from '@/lib/utils'
import CalendarIcon from '@/assets/images/_common/calendar.svg'

export const COMMAND_TABLE_GRID = 'grid grid-cols-[minmax(0,1fr)_96px] gap-x-4 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,1fr)_96px] lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,1.1fr)_96px_120px_minmax(0,1fr)]'

const HEADERS = [
    { label: "Company", className: '' },
    { label: "Segment & Stage", className: 'hidden md:block' },
    { label: "Account owner", className: 'hidden md:block' },
    { label: "Pipeline", className: 'text-right' },
    { label: "Win probability", className: 'hidden text-right lg:block' },
    { label: "Last interaction", className: 'hidden lg:block' }
]

export function CommandTableHeader() {
    const hasCompanies = useCommandState(state => {
        if (!state.search) return true

        for (const [value, score] of state.filtered.items) {
            if (score > 0 && !value.startsWith('page-') && !value.startsWith('action-')) return true
        }

        return false
    })

    if (!hasCompanies) return null

    return (
        <div
            aria-hidden
            className={cn(
                COMMAND_TABLE_GRID,
                'caption-style border-line-strong text-subtle h-9 items-center border-b px-4'
            )}
        >
            {HEADERS.map(header => (
                <span key={header.label} className={cn('truncate', header.className)}>
                    {header.label}
                </span>
            ))}
        </div>
    )
}

interface CommandCompanyRowProps {
    company: Company
    onSelect: () => void
}

export function CommandCompanyRow({ company, onSelect }: CommandCompanyRowProps) {
    const owner = ownerByName(company.owner)
    const { visible, hidden } = splitTags(company.tags)
    const initial = (
        <span className='caption-style text-soft'>
            {company.name.slice(0, 1)}
        </span>
    )

    return (
        <CommandItem
            value={company.id}
            keywords={[company.name, company.owner, ...company.tags]}
            onSelect={onSelect}
            className={cn(COMMAND_TABLE_GRID, 'text-foreground h-11 gap-x-4')}
        >
            <span className='flex min-w-0 items-center gap-2.5'>
                <span className='bg-muted flex size-6 shrink-0 items-center justify-center rounded-md shadow-[0px_0px_0px_1px_#232323]'>
                    {company.logo ? (
                        <Asset
                            src={company.logo}
                            alt=''
                            width={1}
                            height={1}
                            fit='contain'
                            fallback={initial}
                            className='size-3.5'
                        />
                    ) : initial}
                </span>
                <span className='truncate' data-mdlfy-cms={`companies:${company.id}:name`}>
                    {company.name}
                </span>
            </span>

            <span className='hidden min-w-0 items-center gap-[3px] overflow-hidden md:flex'>
                {visible.map(tag => (
                    <Tag key={tag} tone={tagTone(tag)} size='sm'>
                        {tag}
                    </Tag>
                ))}
                {hidden > 0 && (
                    <Tag tone='neutral' size='sm'>
                        +{hidden}
                    </Tag>
                )}
            </span>

            <span className='text-soft hidden min-w-0 items-center gap-1.5 md:flex'>
                <Avatar src={owner.avatar} alt='' />
                <span className='truncate' data-mdlfy-cms={`companies:${company.id}:owner`}>
                    {company.owner}
                </span>
            </span>

            <span className='flex items-center justify-end gap-1 tabular-nums'>
                <span className='text-muted-foreground'>$</span>
                <span>{formatMoney(company.pipelineValue)}</span>
            </span>

            <span className='hidden items-center justify-end gap-2 tabular-nums lg:flex'>
                <SegmentBar percent={company.winProbability} className='w-[60px]' />
                <span className='w-[4ch] text-right'>
                    <span>{company.winProbability}</span>%
                </span>
            </span>

            <span className='text-soft hidden min-w-0 items-center gap-1 lg:flex'>
                <CalendarIcon aria-hidden className='size-3.5 shrink-0' />
                <span className='shrink-0 tabular-nums'>
                    {formatDate(company.lastInteraction.date)}
                </span>
                <span aria-hidden className='mx-[3px] h-2 w-px shrink-0 bg-white/15' />
                <span className='truncate' data-mdlfy-cms={`companies:${company.id}:last_interaction_label`}>
                    {company.lastInteraction.label}
                </span>
            </span>
        </CommandItem>
    )
}
